<template>
  <div v-hotkey="shortcuts" class="agent-conversation">
    <div v-if="isLoading" class="agent-loading">
      <p>Loading agent...</p>
    </div>
    <div v-else-if="agent" class="agent-layout">
      <AgentConversationList
        :conversations="conversations"
        :is-loading="conversationsLoading"
        :active-conversation-id="conversationId"
        @select="onSelectConversation"
        @new-chat="onNewChat"
        @delete="onDeleteConversation"
        @sidebar-resized="(w) => (leftPanelWidth = w)"
      />

      <div class="conversation-main">
        <FindBar
          v-if="isFindBarOpen"
          ref="findBarEl"
          container-id="scrollable-conversation"
          @close="closeFindBar"
        />
        <BaseTopBar
          :left-panel-width="leftPanelWidth"
          :is-navigation-displayed="false"
          with-find
          @find="openFindBar"
        >
          <template #left>
            <Breadcrumb :items="breadcrumbItems" :home="breadcrumbHome" />
          </template>
          <template #right>
            <BaseButton
              kind="auxiliary"
              text="Configuration"
              @click="onToggleConfiguration"
            >
              <template #icon>
                <SettingsIcon />
              </template>
            </BaseButton>
            <ShareButton :export-action="onExport" />
          </template>
          <template #more>
            <BaseButton
              class="context-action"
              kind="auxiliary"
              text="Copy Link"
              tint="none"
              is-expanded
              @click="copyConversationLink"
            >
              <template #icon><CopyLinkIcon /></template>
              <template #right>{{ copyLinkShortcutDisplay }}</template>
            </BaseButton>
            <BaseButton
              class="context-action"
              kind="auxiliary"
              text="Delete"
              tint="red"
              is-expanded
              @click="onDeleteConversation(conversationId)"
            >
              <template #icon><DeleteIcon /></template>
            </BaseButton>
          </template>
        </BaseTopBar>

        <ConversationView
          v-if="activeConversation"
          :session="session"
          :conversation-id="conversationId"
          :active-conversation="activeConversation"
          :workspace-id="workspaceId"
          :organization-id="organizationId"
          :agent-config-context="agentConfigContext"
          :mentions-enabled="mentionsEnabled"
          :reference-search-organization-id="referenceSearchOrganizationId"
          :reference-context-workspace-id="referenceContextWorkspaceId"
          :integration-organization-ids="integrationOrganizationIds"
        />

        <div
          v-else-if="conversationsLoading || activeConversationLoading"
          class="conversation-loading"
        >
          <BaseSkeletonList is-compact :size="8" />
        </div>

        <div v-else class="conversation-not-found">
          <p>Conversation not found.</p>
        </div>
      </div>
    </div>
    <div v-else class="agent-not-found">
      <p>Agent not found or not enabled.</p>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, toRefs } from "vue";
import { onBeforeRouteLeave } from "vue-router";

import controllerModule from "@ctrl/controller";
import type { OrgId, WorkspaceId } from "@ctrl/controller-rpc";

import { copy } from "@/clipboard";
import AgentConversationList from "@/components/ai/AgentConversationList.vue";
import {
  buildConversationDocument,
  collectTodoTaskIds,
  parseRichContent,
  resolveTaskTitles,
  sanitizeAiDocument,
} from "@/components/ai/conversationDocument";
import ConversationView from "@/components/ai/ConversationView.vue";
import { getAgentIntegrationOrganizationIds } from "@/components/ai/integrationOrganizations";
import { useAgent } from "@/components/ai/useAgent";
import type { AgentChatSession } from "@/components/ai/useAgent";
import { useConversations } from "@/components/ai/useConversations";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import BaseTopBar from "@/components/base/BaseTopBar.vue";
import Breadcrumb, {
  type BreadcrumbHome,
  type BreadcrumbItem,
} from "@/components/Breadcrumb.vue";
import FindBar from "@/components/find/FindBar.vue";
import { useFindBar } from "@/components/find/useFindBar";
import CopyLinkIcon from "@/components/icons/CopyLinkIcon.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import * as inject from "@/components/inject";
import { openExportObjectModal } from "@/components/modals/export";
import ShareButton from "@/components/ShareButton.vue";
import {
  assignShortcuts,
  copyLinkItem,
  Shortcuts,
} from "@/components/shortcuts";
import { getOrganizationDefaultWorkspace } from "@/components/workspaces";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { hotkeys } from "@/utils/hotkeys";
import { keymap } from "@/utils/keymap";
import {
  navigateToAgent,
  navigateToAgentConfiguration,
  replaceAgentConversation,
  resolveRouteFullPath,
} from "@/utils/navigate";

import SettingsIcon from "../icons/tabs/SettingsIcon.vue";

const { makeBlockId } = controllerModule;

const unavailableAgentChatSession: AgentChatSession = {
  send: () => Promise.reject(new Error("Agent chat session is unavailable")),
};

export default defineComponent({
  name: "AgentConversation",
  components: {
    AgentConversationList,
    BaseButton,
    BaseSkeletonList,
    BaseTopBar,
    Breadcrumb,
    ConversationView,
    CopyLinkIcon,
    DeleteIcon,
    FindBar,
    SettingsIcon,
    ShareButton,
  },
  props: {
    organizationId: { type: String, required: true },
    agentId: { type: String, required: true },
    conversationId: { type: String, required: true },
  },
  async setup(props) {
    const { organizationId, agentId, conversationId } = toRefs(props);

    const controller = inject.controller();
    const router = inject.router();
    const profile = inject.profile();
    const logger = inject.logger("AgentConversation");
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();

    const leftPanelWidth = ref(252);

    const { isFindBarOpen, findBarEl, openFindBar, closeFindBar } =
      useFindBar();

    const releaser = useReleaser();

    // Flush the agent editors' debounced edits when leaving the page, so the
    // controller has the latest content before the next mount re-fetches the
    // agent. The guard must be registered before the first `await` (it
    // attaches to the active component instance), so the flush is late-bound
    // once the agent loads below.
    let flushAgentEdits: (() => void) | undefined;
    onBeforeRouteLeave(() => flushAgentEdits?.());

    const missingValues =
      !organizationId.value || !agentId.value || !profile.value;
    if (missingValues) {
      logger.debug("AgentConversation", "Missing required values");
      return {
        workspaceId: ref(""),
        leftPanelWidth,
        isFindBarOpen,
        findBarEl,
        openFindBar,
        closeFindBar,
        agent: ref(undefined),
        conversations: ref([]),
        conversationsLoading: ref(true),
        activeConversation: ref(null),
        activeConversationLoading: ref(false),
        isLoading: ref(true),
        agentConfigContext: ref({}),
        mentionsEnabled: ref(false),
        referenceSearchOrganizationId: ref<OrgId | null>(null),
        referenceContextWorkspaceId: ref<WorkspaceId | null>(null),
        onSelectConversation: () => {},
        onNewChat: () => {},
        onDeleteConversation: () => {},
        copyConversationLink: () => {},
        copyLinkShortcutDisplay: "",
        shortcuts: computed<Shortcuts>(() => ({})),
        onExport: () => {},
        onToggleConfiguration: () => {},
        breadcrumbItems: computed<BreadcrumbItem[]>(() => []),
        breadcrumbHome: computed((): BreadcrumbHome | undefined => undefined),
        session: unavailableAgentChatSession,
      };
    }

    const result = await releaser.add(
      useAgent(
        controller,
        profile,
        logger.base(),
        organizationId,
        agentId,
        conversationId,
        "AgentConversation"
      )
    );
    flushAgentEdits = result.flushPendingEdits;

    const {
      agent,
      hierarchy,
      isLoading,
      chat,
      conversationIds,
      agentConfigContext,
    } = result;

    const workspaceId = await releaser.add(
      bindAsync(organizationId, async (orgId) => {
        if (!orgId) return releasableRef<WorkspaceId | undefined>(undefined);
        const wsId = await getOrganizationDefaultWorkspace(controller, orgId);
        return releasableRef<WorkspaceId | undefined>(wsId);
      })
    );

    const breadcrumbHome = computed((): BreadcrumbHome | undefined => {
      const oid: OrgId = organizationId.value;
      const personalOid: OrgId | undefined =
        profile.value?.user.personal.organizationId;
      if (personalOid && oid !== personalOid) {
        return { type: "organization", id: oid };
      }
      const wsId = workspaceId.value;
      if (wsId) return { type: "workspace", id: wsId };
      return undefined;
    });

    const mentionsEnabled = computed(() => {
      if (!workspaceId.value) return false;
      if (!profile.value.user.personal.workspaceId) return false;
      return workspaceId.value! !== profile.value.user.personal.workspaceId!;
    });
    const isAssistantAgent = computed(() => agent.value?.key === "assistant");
    const integrationOrganizationIds = computed<OrgId[] | undefined>(() => {
      return getAgentIntegrationOrganizationIds(
        profile.value,
        isAssistantAgent.value
      );
    });
    const referenceSearchOrganizationId = computed<OrgId | null>(() =>
      isAssistantAgent.value ? null : organizationId.value
    );
    const referenceContextWorkspaceId = computed<WorkspaceId | null>(() =>
      isAssistantAgent.value
        ? (profile.value.user.personal.workspaceId ?? null)
        : (workspaceId.value ?? null)
    );

    const { conversations, isLoading: conversationsLoading } = releaser.add(
      useConversations(controller, organizationId, conversationIds)
    );

    // The sidebar list above only holds summaries (no history); the active
    // conversation is fetched in full here, and re-fetched when the
    // conversationId prop changes (the route keeps this component mounted
    // across conversation switches).
    const activeConversationLoading = ref(false);
    const activeConversationRef = await releaser.add(
      bindAsync(conversationId, async (id) => {
        activeConversationLoading.value = true;
        try {
          return await controller.ai.agent.conversation.get(
            organizationId.value,
            id
          );
        } finally {
          activeConversationLoading.value = false;
        }
      })
    );

    const activeConversation = computed(() => {
      const conversation = activeConversationRef.value;
      // While a switch is re-binding, the ref may still expose the previous
      // conversation: show the loading state rather than the wrong content.
      return conversation && conversation.id === conversationId.value
        ? conversation
        : null;
    });

    const session = chat();

    const onSelectConversation = async (id: string) => {
      await replaceAgentConversation(router, agentId.value, id);
    };

    const onNewChat = async () => {
      await navigateToAgent(router, agentId.value);
    };

    const onDeleteConversation = async (id: string) => {
      try {
        await controller.ai.agent.conversation.delete(organizationId.value, id);
        // Navigate away if deleting the active conversation
        if (id === conversationId.value) {
          await navigateToAgent(router, agentId.value);
        }
      } catch (err) {
        logger.error("Failed to delete conversation", err);
      }
    };

    const onToggleConfiguration = async () => {
      await navigateToAgentConfiguration(router, agentId.value);
    };

    const copyConversationLink = async () => {
      const path = resolveRouteFullPath(router, {
        name: RouteNames.AgentConversation,
        params: { agentId: agentId.value },
        query: { id: conversationId.value },
      });
      await copy(`https://open.routine.co?path=${path}`);
      controller.notifications.notify("Link copied to clipboard");
    };

    const copyLinkShortcutDisplay =
      hotkeys.format.stringify(
        [keymap.keyMod, keymap.keyShift, keymap.keyL],
        keymap.formatSymbol,
        keymap.separatorSpace
      ) ?? "";

    const shortcuts = computed<Shortcuts>(() =>
      assignShortcuts(
        { isModalShown, isInputFocused },
        copyLinkItem,
        () => void copyConversationLink()
      )
    );

    const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
      const items: BreadcrumbItem[] = [...hierarchy.value];
      items.push({
        title: "Agents",
        link: {
          name: RouteNames.SettingsAgents,
          params: { orgId: organizationId.value },
        },
      });
      if (agent.value) {
        items.push({
          title: agent.value.name ?? "Agent",
          link: { name: RouteNames.Agent, params: { agentId: agentId.value } },
        });
      }
      if (activeConversation.value) {
        items.push({
          title: activeConversation.value.title || "New Conversation",
          link: { params: {} },
        });
      }
      return items;
    });

    const onExport = async () => {
      const title = activeConversation.value?.title || "New Conversation";
      const history = activeConversation.value?.history ?? [];
      const richMessages = history.map((msg) => {
        if (msg.role !== "assistant") return msg;
        const rich = parseRichContent(msg.content) ?? undefined;
        return rich
          ? { ...msg, document: rich.document, citations: rich.citations }
          : msg;
      });
      const taskTitles = await resolveTaskTitles(
        controller,
        richMessages.flatMap((msg) =>
          "document" in msg && msg.document
            ? collectTodoTaskIds(msg.document)
            : []
        )
      );
      const displayMessages = richMessages.map((msg) =>
        "document" in msg && msg.document
          ? {
              ...msg,
              document: sanitizeAiDocument(msg.document, (id) =>
                taskTitles.get(id)
              ),
            }
          : msg
      );
      openExportObjectModal({
        title,
        properties: [],
        notes: buildConversationDocument(
          displayMessages,
          makeBlockId,
          agent.value?.name ?? "Assistant",
          logger.base()
        ),
        propertiesSelector: false,
      });
    };

    return {
      agent,
      workspaceId,
      isLoading,
      leftPanelWidth,
      isFindBarOpen,
      findBarEl,
      openFindBar,
      closeFindBar,
      onExport,
      onToggleConfiguration,
      conversations,
      conversationsLoading,
      activeConversation,
      activeConversationLoading,
      onSelectConversation,
      onNewChat,
      onDeleteConversation,
      copyConversationLink,
      copyLinkShortcutDisplay,
      shortcuts,
      breadcrumbItems,
      breadcrumbHome,
      session,
      agentConfigContext,
      mentionsEnabled,
      referenceSearchOrganizationId,
      referenceContextWorkspaceId,
      integrationOrganizationIds,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/agent" as *;
@use "@/styles/context-actions.scss";

.agent-conversation {
  @include agent-root;

  .agent-layout {
    @include agent-sidebar-layout;
  }

  .conversation-main {
    @include agent-main-pane;
    // Anchor for the find bar, which must not scroll with the chat history.
    position: relative;
  }
}

.conversation-not-found,
.agent-not-found,
.agent-loading {
  @include agent-state-panel;
}

.conversation-loading {
  flex: 1;
  padding: 24px;
  overflow: hidden;
}
</style>
